import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Accordion } from '@/design-system/atoms/Accordion';
import { Button } from '@/design-system/atoms/Button';
import { Checkbox } from '@/design-system/atoms/Forms/Checkbox';
import { RadioGroup } from '@/design-system/atoms/Forms/RadioGroup';
import { Radio } from '@/design-system/atoms/Forms/RadioGroup/Radio/Radio';
import { LazyIconX } from '@/design-system/atoms/Icons/IconX/Lazy';
import { LazyIconXCircle } from '@/design-system/atoms/Icons/IconXCircle/Lazy';
import { useSearchParams } from '@/hooks/useSearchParams';

import {
    useProviderSearchContext,
    useProviderSearchZipCityContext,
} from '../../../contexts/ProviderSearchContext';
import { useProviderUIContext } from '../../../contexts/ProviderUIContext';
import { useParsedSearchTerm } from '../hooks/useParsedSearchTerm';
import styles from '../ProviderFilter.module.scss';

const ProviderFilterDesktop = ({
    accordionItems,
    sortValue,
    sortCriteria,
    handleSort,
    distance,
    setDistance,
    disabledDistanceOptions,
    handleSingleFilter,
    handleResetAll,
    disableNextAvailableSort,
    showVpcCheckbox,
}) => {
    const { resetAllLabel, resetAllAriaLabel } = useProviderUIContext();
    const searchParams = useSearchParams();
    const { zipCode } = useProviderSearchZipCityContext();

    const {
        coveo: {
            controller: { breadcrumbManager, querySummary },
        },
        handleMultipleFilters,
        setCoveoAnalytics,
    } = useProviderSearchContext();

    const [state, setState] = useState();
    const [acceptingNew, setAcceptingNew] = useState(false);
    const [schedule, setSchedule] = useState(false);
    const [vpc, setVpc] = useState(false);
    const searchTerm = useParsedSearchTerm(querySummary);

    useEffect(() => {
        return breadcrumbManager.subscribe(() => setState(breadcrumbManager.state));
    }, [breadcrumbManager]);

    useEffect(() => {
        const searchParamNew = searchParams.get('new');
        const searchParamSchedule = searchParams.get('schedule');
        const searchParamVpc = searchParams.get('vpc');

        setAcceptingNew(searchParamNew === 'true' || searchParamNew === true);
        setSchedule(searchParamSchedule === 'true' || searchParamSchedule === true);
        setVpc(searchParamVpc === 'true');
    }, [searchParams]);

    const analyticsObj = useMemo(
        () => ({
            componentName: 'Filter Sort Panel',
            componentTitle: 'filter_panel',
            allFilters: searchParams,
            interactionType: 'filter_reset',
        }),
        [searchParams],
    );

    const filteredSortCriteria = useMemo(() => {
        return sortCriteria
            .map(([name]) => {
                if (
                    (name === 'Distance' && disabledDistanceOptions) ||
                    (name === 'Next Available' && disableNextAvailableSort)
                ) {
                    return null;
                }
                return name;
            })
            .filter(Boolean);
    }, [sortCriteria, disabledDistanceOptions, disableNextAvailableSort]);

    /**
     * Reset a single filter
     * @param {string} facetId - The facet id to reset
     * @param {string} value - The value to reset
     * @returns {void}
     */
    const handleResetFilter = useCallback(
        (facetId, facetValue) => {
            if (!facetId || !facetValue) {
                console.error('ProviderFacetRoot - invalid facetValue');
                return;
            }

            setCoveoAnalytics('facetBreadcrumb', { facetId: facetId, facetValue: facetValue });

            handleMultipleFilters(
                {
                    [facetId]: `${facetValue.value};${facetValue.numberOfResults}`,
                },
                {
                    ...analyticsObj,
                    actionLabel: facetValue.value,
                },
            );
        },
        [handleMultipleFilters, analyticsObj, setCoveoAnalytics],
    );

    /**
     * Handle checkbox toggle to update the search params and coveo analytics
     * @param {string} name - The input name
     * @param {string} label - The input label for analytics
     * @param {boolean} isSelected - The new status of the input
     * @returns {void}
     */
    const handleCheckbox = useCallback(
        (name, label, isSelected) => {
            setCoveoAnalytics(isSelected ? 'staticFilterSelect' : 'staticFilterDeselect', {
                staticFilterId: name,
                staticFilterValue: {
                    caption: label,
                    expression: isSelected ? `${name}=true` : '',
                },
            });

            handleSingleFilter(
                { [name]: isSelected },
                {
                    componentName: 'Filter Sort Panel',
                    componentTitle: 'filter_panel',
                    eventName: 'search',
                    actionLabel: label,
                    allFilters: searchParams,
                    searchTerm: searchTerm || '',
                    zipCode: zipCode || '',
                },
            );
        },
        [handleSingleFilter, setCoveoAnalytics, searchParams, searchTerm, zipCode],
    );

    const handleDistanceChange = useCallback(
        (value) => {
            setDistance(value);

            setCoveoAnalytics(
                value ? 'staticFilterSelect' : 'staticFilterClearAll',
                value
                    ? {
                          staticFilterId: 'distance',
                          staticFilterValue: {
                              caption: `Within ${value} miles`,
                              expression: `distance=${value}`,
                          },
                      }
                    : { staticFilterId: 'distance' },
            );

            handleSingleFilter(
                { distance: value },
                {
                    componentTitle: 'Distance',
                    actionLabel: value ? `Within ${value} miles` : 'Any',
                },
            );
        },
        [handleSingleFilter, setDistance, setCoveoAnalytics],
    );

    return (
        <form className={styles[`provider-filter`]}>
            {(state?.hasBreadcrumbs || distance) && (
                <div className={styles[`provider-filter__active-filters`]}>
                    <Button
                        buttonStyle={Button.STYLE.TEXT}
                        className={styles[`provider-filter__active-filters--reset`]}
                        type="button"
                        iconComponent={LazyIconXCircle}
                        iconRight
                        label={resetAllLabel}
                        ariaLabel={resetAllAriaLabel}
                        onClick={handleResetAll}
                        analytics={analyticsObj}
                    />
                    {state?.hasBreadcrumbs && (
                        <div className={styles['provider-filter__active-filters--list']}>
                            {state?.facetBreadcrumbs?.flatMap((filter) =>
                                filter.values
                                    .filter((item) => item.value.state === 'selected')
                                    .map((item) => (
                                        <Button
                                            key={item.value.value}
                                            label={item.value.value}
                                            ariaLabel={`Remove ${item.value.value} filter`}
                                            onClick={() =>
                                                handleResetFilter(filter.facetId, item.value)
                                            }
                                            buttonStyle={Button.STYLE.FILTER}
                                            iconComponent={LazyIconX}
                                            iconSize="medium"
                                            type="button"
                                        />
                                    )),
                            )}
                        </div>
                    )}
                </div>
            )}

            <fieldset
                className={classNames(
                    styles[`provider-filter__fieldset`],
                    styles[`provider-filter__wrapper`],
                )}
            >
                <legend className={classNames(styles[`provider-filter__legend`], 'type-t3')}>
                    Filters apply upon selection.
                </legend>
                {filteredSortCriteria.length > 1 && (
                    <fieldset className={styles[`provider-filter__fieldset`]}>
                        <legend
                            className={classNames(
                                styles[`provider-filter__group-legend`],
                                'type-e1',
                            )}
                        >
                            Sort By:
                        </legend>
                        {/* key={sortValue} forces remount when Coveo sort state changes (e.g. URL-parsed
                            back-nav), so defaultValue always seeds from the current criterion. */}
                        {sortValue && (
                            <RadioGroup
                                key={sortValue}
                                className={styles[`provider-filter__radio-group`]}
                                defaultValue={sortValue}
                                onChange={handleSort}
                            >
                                {filteredSortCriteria.map((name) => (
                                    <Radio key={name} value={name} label={name} />
                                ))}
                            </RadioGroup>
                        )}
                    </fieldset>
                )}
                <fieldset className={styles[`provider-filter__fieldset`]}>
                    <legend
                        className={classNames(
                            styles[`provider-filter__group-legend`],
                            styles[`provider-filter__container`],
                            'type-e1',
                        )}
                    >
                        Filter By:
                    </legend>
                    <div className={styles['provider-filter__toggles']}>
                        <Checkbox
                            name="new"
                            label="Accepting New Patients"
                            onChange={(isSelected) =>
                                handleCheckbox('new', 'Accepting New Patients', isSelected)
                            }
                            defaultSelected={false}
                            isSelected={acceptingNew}
                        />
                        <Checkbox
                            name="schedule"
                            label="Schedule Online"
                            onChange={(isSelected) =>
                                handleCheckbox('schedule', 'Schedule Online', isSelected)
                            }
                            defaultSelected={false}
                            isSelected={schedule}
                        />
                        {showVpcCheckbox && (
                            <Checkbox
                                name="vpc"
                                label="Virtual Primary Care"
                                onChange={(isSelected) =>
                                    handleCheckbox('vpc', 'Virtual Primary Care', isSelected)
                                }
                                defaultSelected={false}
                                isSelected={vpc}
                            />
                        )}
                    </div>
                    {!disabledDistanceOptions && (
                        <div className={styles[`provider-filter__distance-container`]}>
                            <RadioGroup
                                label="Distance"
                                labelClass={styles[`provider-filter__radio-group-label`]}
                                value={distance}
                                onChange={(value) => handleDistanceChange(value)}
                            >
                                <Radio value="" label="Any" />

                                <>
                                    <Radio value="2" label="Within 2 miles" />
                                    <Radio value="5" label="Within 5 miles" />
                                    <Radio value="10" label="Within 10 miles" />
                                    <Radio value="20" label="Within 20 miles" />
                                </>
                            </RadioGroup>
                        </div>
                    )}
                </fieldset>
                <Accordion
                    type={'multiple'}
                    className={styles[`provider-filter__accordion`]}
                    accordionItems={accordionItems}
                    noScrollLogic
                />
            </fieldset>
        </form>
    );
};

ProviderFilterDesktop.propTypes = {
    accordionItems: PropTypes.array,
    sortValue: PropTypes.string,
    sortCriteria: PropTypes.array,
    handleSort: PropTypes.func,
    distance: PropTypes.string,
    setDistance: PropTypes.func,
    disabledDistanceOptions: PropTypes.bool,
    handleSingleFilter: PropTypes.func,
    handleResetAll: PropTypes.func,
    disableNextAvailableSort: PropTypes.bool,
    showVpcCheckbox: PropTypes.bool,
};

export default ProviderFilterDesktop;
